import {
  attendusByIds,
  attendusFor,
  attenduShortLabel,
  hasReferentiel,
} from '../../lib/referentiel.ts';
import { asLevel } from '../../lib/levels.ts';
import { useStore } from '../../store/store.ts';
import type { SlotIndex } from '../../store/types.ts';
import { pushModal } from '../../ui/ui-store.ts';
import { CompetencePickerModal } from './CompetencePickerModal.tsx';

interface Props {
  /** The lesson's subject: it decides which référentiel is offered. */
  subject: string;
  /** Rituals (-1) are not tied to a discipline, so they get no picker. */
  slot: SlotIndex;
  value: string[];
  onChange: (ids: string[]) => void;
}

/**
 * The chosen attendus on the lesson form, and the way into the picker.
 *
 * The list itself lives in a dialog of its own (`CompetencePickerModal`): the
 * modal stack keeps this form mounted underneath, so nothing typed into it is
 * lost while the teacher browses the référentiel.
 */
export function CompetenceField({ subject, slot, value, onChange }: Props) {
  const level = asLevel(useStore((s) => s.settings.className));
  const chosen = attendusByIds(value);
  const available = attendusFor(subject, level);

  function open() {
    pushModal(
      `Référentiel ${level} — ${subject}`,
      <CompetencePickerModal
        subject={subject}
        level={level}
        initial={value}
        onConfirm={onChange}
      />,
    );
  }

  if (slot === -1) {
    return (
      <div>
        <label>Compétences du tronc commun</label>
        <p class="small muted" style={{ margin: '6px 0 0' }}>
          Les rituels ne sont pas rattachés à une discipline.
        </p>
      </div>
    );
  }

  if (!hasReferentiel(level)) {
    return (
      <div>
        <label>Compétences du tronc commun</label>
        <p class="small muted" style={{ margin: '6px 0 0' }}>
          Le référentiel n’est disponible que pour la P3. Change la classe dans « Compte » pour
          l’utiliser.
        </p>
      </div>
    );
  }

  if (available.length === 0) {
    return (
      <div>
        <label>Compétences du tronc commun</label>
        <p class="small muted" style={{ margin: '6px 0 0' }}>
          Pas d’attendus du tronc commun pour « {subject} ».
        </p>
      </div>
    );
  }

  return (
    <div>
      <label>Compétences du tronc commun</label>

      {chosen.length > 0 && (
        <div class="competenceChips">
          {chosen.map((attendu) => (
            <span class="tag competenceChip" key={attendu.id}>
              <span title={attendu.text}>{attenduShortLabel(attendu)}</span>
              <button
                type="button"
                class="competenceChipRemove"
                aria-label={`Retirer : ${attenduShortLabel(attendu, 60)}`}
                onClick={() => onChange(value.filter((id) => id !== attendu.id))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        class="btn soft"
        style={{ marginTop: chosen.length > 0 ? '8px' : '4px' }}
        onClick={open}
      >
        {chosen.length > 0 ? 'Modifier la sélection' : 'Choisir dans le référentiel'}
      </button>
    </div>
  );
}
